import classNames from 'classnames'
import { AnimatePresence, motion, Variants } from 'framer-motion'
import React from 'react'
import { FieldError, UseFormRegister } from 'react-hook-form'

import { commonIcons } from '@apps-common/config/icons'

export const INPUT_INFO_SM_NO_BORDER = 'border-none rounded-[5px] px-[7.5px] pb-[5px] jost-light !opacity-100'
export const INPUT_INFO_SM_NO_BORDER_NO_BG = 'bg-transparent border-none rounded-[5px] px-[7.5px] pb-[5px] jost-light !opacity-100'

const variants: Variants = {
  initial: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2
    }
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2
    }
  }
}

type InputProps = {
  id: string
  containerClassName?: string
  innerContainerClassName?: string
  label: string
  showLabel?: boolean
  labelClassName?: string
  className?: string
  placeholder?: string
  fixedLoading?: boolean
  loading?: boolean
  hideNoEditIcon?: boolean
  value: any
  registration?: Partial<UseFormRegister<any>>
  errors?: FieldError
  info?: string
  requiredAsterisk?: boolean
}

export const InputInfoSM: React.FC<InputProps> = (props) => {
  const {
    id,
    className,
    containerClassName,
    innerContainerClassName,
    showLabel = false,
    labelClassName,
    label,
    placeholder,
    fixedLoading,
    loading,
    hideNoEditIcon,
    value,
    registration,
    errors,
    info,
    requiredAsterisk
  } = props
  const EditOffIcon = commonIcons.editOff

  return (
    <AnimatePresence mode='wait'>
      {loading && (
        <motion.div
          key={`${id}-input-info-sm-skeleton`}
          initial='initial'
          animate='animate'
          exit='exit'
          variants={variants}
          className={classNames('mb-2', !fixedLoading && 'animate-pulse', containerClassName)}
        >
          <div
            className={classNames('block ml-1 mb-2 h-[16px] !bg-skeleton max-w-[20%] rounded-full', labelClassName, {
              'sr-only': !showLabel
            })}
          />
          <div className='relative'>
            <div className={classNames('px-4 py-2 rounded-3xl !bg-skeleton w-full h-[36px]', className)} />
          </div>
          <p className='pl-2 pt-2 h-[5px]' />
        </motion.div>
      )}
      {!loading && (
        <motion.div
          key={`${id}-input-info-sm`}
          initial='initial'
          animate='animate'
          exit='exit'
          variants={variants}
          className={classNames('mb-2', containerClassName)}
        >
          <label className={classNames('block pl-2 mb-2 text-sm text-text-muted font-bold', labelClassName, { 'sr-only': !showLabel })}>
            {label} {requiredAsterisk && <span className='relative left-[-2px] text-error text-[1rem]'>*</span>}
          </label>
          <div className={classNames(innerContainerClassName, 'relative')}>
            <div
              className={classNames(
                'px-4 py-2 rounded-3xl bg-surface/30 text-text/50 w-full text-sm border border-border hover:cursor-not-allowed',
                className
              )}
            >
              {value || <span className='opacity-30'>{placeholder}</span>}
            </div>
            {!hideNoEditIcon && <EditOffIcon className='absolute right-[10px] top-[10px] text-text opacity-40' />}
          </div>
          <p
            className={classNames(
              'pl-2 pt-2 text-[12px]',
              info && !errors?.message && 'text-text-muted/70',
              (!info || errors?.message) && 'text-error'
            )}
          >
            {errors?.message || info}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
